// Client-side mirror of the server's Get-AtcOperatorRole (ATC-049): resolve an
// operator's role from their AAD group claims. This is a UX convenience only —
// the SERVER is the security boundary (every endpoint enforces the ops-group
// check in code). The SPA uses this to hide/disable actions the operator can't
// perform, not to grant access.
//
// v1 maps membership in the single Rubix ops group to a full-access operator
// role; the shape is tier-ready (viewer / operator / billing-admin) per
// ATC-ADR-014 without changing callers that check `capabilities`.

export type OperatorRoleName = 'operator' | 'none'
export type Capability = 'read' | 'write' | 'billing'

export interface OperatorRole {
  role: OperatorRoleName
  isOperator: boolean
  capabilities: Capability[]
}

const NO_ROLE: OperatorRole = { role: 'none', isOperator: false, capabilities: [] }

/**
 * @param groupClaims the `groups` claim from the id token (array of AAD group Object IDs), may be undefined
 * @param opsGroupId  the Rubix ops group Object ID from runtime config
 */
export function resolveOperatorRole(
  groupClaims: string[] | undefined,
  opsGroupId: string,
): OperatorRole {
  if (!opsGroupId) {
    return NO_ROLE
  }
  const groups = groupClaims ?? []
  if (groups.includes(opsGroupId)) {
    return { role: 'operator', isOperator: true, capabilities: ['read', 'write', 'billing'] }
  }
  return NO_ROLE
}

export function hasCapability(role: OperatorRole, capability: Capability): boolean {
  return role.capabilities.includes(capability)
}
